import {
  Paper,
  Grid,
  Button,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import style from "./control-bar.module.scss";
import roundedButtonStyle from "styles/buttons/rounded-button.module.scss";
import PaletteIcon from "@material-ui/icons/Palette";
import { ColorPickerToolTip } from "../../shared/color-picker-tooltip";
import {
  GetUrlBuilderData,
  SetUrlBuilderData,
  UpdateBuilderPermissions,
} from "store/url-builder";
import { useDispatch, useSelector } from "react-redux";
import { postPrimaryColor, setBuilderPermissions } from "adapters";
import { postCustomCTA, postPublish } from "./../../../adapters/url-builder";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { UserData } from "types/userData";
import { PublishDialog } from "../publish-dialog";
import { GetCardData } from "store/card";
import { BuilderPermissions, ObjectPermissionSetData } from "types/permissions";
import { updateCard, updateCardsByTemplate } from "adapters/public-card";

export interface DesignContorlBarProps {
  builderType: string;
  userData: UserData;
  permissions: ObjectPermissionSetData[];
  permissionSetId: number;
}

export const DesignerControlBar = ({
  builderType,
  userData,
  permissions,
  permissionSetId,
}: DesignContorlBarProps) => {
  const builderData = useSelector(GetUrlBuilderData);
  const cardData = useSelector(GetCardData);
  const dispatch = useDispatch();
  const [selectedColor, setSelectedColor] = useState(builderData.primaryColor);
  const [loading, setLoading] = useState(false);
  const { cardId }: { cardId: string } = useParams();
  const userPlan = userData.userPlan;
  const [openDialog, setOpenDialog] = useState(false);

  const [buttonText, setButtonText] = useState("Publish");

  const [permission, setPermission] = useState<ObjectPermissionSetData>({
    objectPermissionSetId: 0,
    objectId: BuilderPermissions.colourPicker,
    objectPermissionId: 2,
    objectPermissionValue: true,
    object: {
      objectId: BuilderPermissions.colourPicker,
      objectName: "colourPicker",
      parentObjectId: 1,
    },
    objectPermission: {
      objectPermissionId: 2,
      objectPermissionName: "changeable",
    },
  });

  const savePrimaryColor = (color: string) => {
    if (color === selectedColor.color) return;

    postPrimaryColor(cardId, builderType, {
      color,
    }).then(({ data }) => {
      if (!data.success) return;
      dispatch(SetUrlBuilderData(data.data));
      setSelectedColor({ color: color });
    });
  };

  const publish = () => {
    if (userPlan === "FREE" && builderType !== "ENTERPRISE_TEMPLATE") {
      setOpenDialog(true);
    } else {
      publishGo();
    }
  };

  const onClosePublishDialog = (result: string) => {
    setOpenDialog(false);
    if (result === "PUBLISH") {
      publishGo();
    }
  };

  const publishGo = () => {
    setLoading(true);
    setButtonText("Publishing...");
    postPublish(cardId, builderType, cardData.templateId.toString())
      .then(async ({ data }) => {
        dispatch(
          SetUrlBuilderData({ ...builderData, published: data.data.published })
        );

        if (builderType === "PRO") {
          setButtonText("Updating card");
          await publishCard();
        } else {
          setButtonText("Updating cards");
          await publishCards();
        }

        if (data.success) toast.success(data.message);
      })
      .finally(() => {
        setLoading(false);
        setButtonText("Publish");
      });
  };

  //a custom api is calling to get publish buttom enabled from response
  const enablePublish = async () => {
    await postCustomCTA(cardId, builderType, builderData.customCTA)
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
      })
      .catch((err) => {
        toast.error("Error while enabling publish");
      });
  };

  //update public card
  const publishCard = async () => {
    const imgExt = cardData.cardImage
      ? cardData.cardImage.split(".")[1]
        ? cardData.cardImage.split(".")[1]
        : ""
      : "";

    await updateCard(cardId, cardData.qrcode, imgExt).catch(async (err) => {
      await enablePublish();
      toast.error("An error occured while upadating");
    });
  };

  //update public card
  const publishCards = async () => {
    await updateCardsByTemplate(cardId).catch(async (err) => {
      await enablePublish();
      toast.error("An error occured while upadating cards");
    });
  };

  const openPortfolio = () => {
    console.log("openPortfolio");
    window
      .open(
        `${window.location.origin}/pro/cards/url_designer/portfolio/${builderData.cardId}`,
        "_blank"
      )
      ?.focus();
  };
  const openPortfolioPreview = () => {
    console.log("openPortfolioPreview");
    window
      .open(
        `${window.location.origin}/pro/cards/url_designer/portfolio/${builderData.cardId}/preview`,
        "_blank"
      )
      ?.focus();
  };

  const updatePermission = async (permission: ObjectPermissionSetData) => {
    const updatedPermissionData = await setBuilderPermissions(
      cardId,
      permission
    );
    if (!updatedPermissionData.data.success)
      throw new Error(updatedPermissionData.data.message);

    if (permissions.filter((p) => p.objectPermissionId === 2).length !== 1)
      setPermission((prev) => {
        return {
          ...prev,
          objectPermissionValue: !prev.objectPermissionValue,
        };
      });

    dispatch(UpdateBuilderPermissions(updatedPermissionData.data.data));
  };

  useEffect(() => {
    const _filteredPermissions = permissions.filter(
      (p) => p.objectPermissionId === 2
    );

    if (_filteredPermissions.length === 1) {
      setPermission(_filteredPermissions[0]);
    }
  }, [permissions]);

  useEffect(() => {
    if (permission.objectPermissionSetId !== permissionSetId)
      setPermission((prev) => {
        return {
          ...prev,
          objectPermissionSetId: permissionSetId,
        };
      });
  }, [permissionSetId, permission.objectPermissionSetId]);

  return (
    <Paper className={style.root}>
      <Grid
        className={style.grid}
        container
        xs={12}
        md={8}
        style={{ margin: "auto" }}
      >
        {(builderType === "ENTERPRISE_TEMPLATE" ||
          (builderType !== "ENTERPRISE_TEMPLATE" &&
            permission.objectPermissionValue === true)) && (
          <Grid
            container
            item
            xs={12}
            sm={builderType === "PRO" ? 5 : 8}
            justifyContent="center"
            alignItems="center"
            style={{ marginBottom: 5 }}
          >
            <ColorPickerToolTip
              selectedColor={selectedColor.color}
              onColorSelect={savePrimaryColor}
            >
              <Button
                className={style.colorPickerLabel}
                style={{ color: "#fff", backgroundColor: "#aaaaaa" }}
              >
                <PaletteIcon /> Choose element color
              </Button>
            </ColorPickerToolTip>

            {builderType === "ENTERPRISE_TEMPLATE" && (
              <Box style={{ marginLeft: "10px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={permission.objectPermissionValue}
                      onChange={() =>
                        updatePermission({
                          ...permission,
                          objectPermissionValue:
                            !permission.objectPermissionValue,
                        })
                      }
                      color="primary"
                    />
                  }
                  label={"Changeable"}
                  style={{ color: "white" }}
                />
              </Box>
            )}
          </Grid>
        )}

        <Grid
          container
          item
          xs={12}
          sm={
            builderType === "ENTERPRISE_TEMPLATE"
              ? 4
              : permission.objectPermissionValue === true
              ? 7
              : 12
          }
          // sm={builderType === "PRO" ? 7 : 4}
          justifyContent="center"
          style={{ marginBottom: 5, gap: "10px 0px" }}
        >
          {builderType == "PRO" ? (
            <Button
              onClick={openPortfolioPreview}
              // className='mr-2'
              className={roundedButtonStyle.root}
              style={{
                color: "#fff",
                backgroundColor: "#aaaaaa",
                marginRight: "10px",
              }}
            >
              Preview
            </Button>
          ) : (
            ""
          )}
          {builderType == "PRO" ? (
            <Button
              onClick={openPortfolio}
              // className='mr-2'
              className={roundedButtonStyle.root}
              style={{
                color: "#fff",
                backgroundColor: "#aaaaaa",
                marginRight: "10px",
              }}
            >
              View as Guest
            </Button>
          ) : (
            ""
          )}
          {/* { (builderType == 'PRO')? ( */}
          <Button
            onClick={publish}
            variant="contained"
            color="primary"
            disabled={loading || builderData.published.published}
            className={roundedButtonStyle.root}
            style={
              loading || builderData.published.published
                ? { color: "#555", backgroundColor: "#aaa" }
                : { color: "#fff", backgroundColor: "#35bb35" }
            }
          >
            <span className="d-flex align-items-center justify-content-center">
              {loading && (
                <CircularProgress
                  size="20px"
                  className="mr-2"
                  style={{ color: "white" }}
                />
              )}
              {buttonText}
            </span>
          </Button>
          {/* ):""} */}
        </Grid>
        {openDialog && (
          <PublishDialog
            isOpen={openDialog}
            onClose={onClosePublishDialog}
            builderType={builderType}
          />
        )}
      </Grid>
    </Paper>
  );
};
